import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Car, 
  ArrowLeft, 
  Calendar, 
  DollarSign, 
  Clock, 
  Users,
  CheckCircle,
  XCircle,
  AlertCircle,
  RefreshCw,
  Trash2,
  MapPin,
  Navigation,
  LogOut
} from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import AdminLogin from "./AdminAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  
  // Auth states
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);

  // Check existing auth on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/admin/me`, { withCredentials: true });
        if (response.data?.authenticated) {
          setIsAuthenticated(true);
        }
      } catch (error) {
        setIsAuthenticated(false);
      } finally {
        setCheckingAuth(false);
      }
    };

    checkAuth();
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [statusFilter, isAuthenticated]);

  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/admin/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error("Logout error:", error);
    }
    setIsAuthenticated(false);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [bookingsRes, statsRes] = await Promise.all([
        axios.get(`${API}/bookings${statusFilter !== "all" ? `?status=${statusFilter}` : ""}`),
        axios.get(`${API}/stats`)
      ]);
      setBookings(bookingsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error("Error loading data");
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (bookingId, newStatus) => {
    try {
      await axios.patch(`${API}/bookings/${bookingId}/status?status=${newStatus}`);
      toast.success(`Booking ${newStatus}`);
      fetchData();
    } catch (error) {
      toast.error("Error updating status");
    }
  };

  const deleteBooking = async (bookingId) => {
    if (!window.confirm("Are you sure you want to delete this booking?")) return;
    
    try {
      await axios.delete(`${API}/bookings/${bookingId}`);
      toast.success("Booking deleted");
      fetchData();
    } catch (error) {
      toast.error("Error deleting booking");
    }
  };

  const getStatusBadge = (status) => {
    const classes = {
      pending: "status-pending",
      confirmed: "status-confirmed",
      completed: "status-completed",
      cancelled: "status-cancelled"
    };
    return (
      <span className={cn("status-badge", classes[status])}>
        {status}
      </span>
    );
  };

  const statCards = stats ? [
    {
      label: "Total Bookings",
      value: stats.total_bookings,
      icon: <Calendar className="w-5 h-5" />,
      color: "text-white"
    },
    {
      label: "Pending",
      value: stats.pending_bookings,
      icon: <AlertCircle className="w-5 h-5" />,
      color: "text-yellow-500"
    },
    {
      label: "Confirmed",
      value: stats.confirmed_bookings,
      icon: <CheckCircle className="w-5 h-5" />,
      color: "text-green-500"
    },
    {
      label: "Revenue",
      value: `$${stats.total_revenue.toFixed(2)}`,
      icon: <DollarSign className="w-5 h-5" />,
      color: "text-[#D4AF37]"
    }
  ] : [];

  // Show loading while checking auth
  if (checkingAuth) {
    return (
      <div className="min-h-screen quiet-luxury-bg flex items-center justify-center">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4"></div>
          <p className="text-silver/50">Loading...</p>
        </div>
      </div>
    );
  }

  // Show login if not authenticated
  if (!isAuthenticated) {
    return <AdminLogin onSuccess={handleLoginSuccess} />;
  }

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="admin-dashboard">
      {/* Header */}
      <header className="glass sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link 
              to="/" 
              className="text-white/50 hover:text-white transition-colors"
              data-testid="back-to-home-link"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#C5A572] flex items-center justify-center">
                <span className="font-serif text-black font-bold text-lg">IV</span>
              </div>
              <div>
                <span className="font-serif text-xl text-white">Admin Dashboard</span>
                <p className="text-xs text-white/50">Administrator</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button 
              onClick={fetchData}
              className="btn-secondary flex items-center gap-2"
              data-testid="refresh-btn"
            >
              <RefreshCw className={cn("w-4 h-4", loading && "animate-spin")} />
              Refresh
            </button>
            <button 
              onClick={handleLogout}
              className="text-white/50 hover:text-white transition-colors flex items-center gap-2 text-sm"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-10">
          {statCards.map((stat, index) => (
            <div 
              key={index}
              className="glass-light rounded-md p-6"
              data-testid={`stat-card-${index}`}
            >
              <div className="flex items-center justify-between mb-4">
                <span className="text-white/40 text-xs uppercase tracking-widest">
                  {stat.label}
                </span>
                <div className={stat.color}>{stat.icon}</div>
              </div>
              <p className={cn("text-3xl font-serif", stat.color)}>
                {stat.value}
              </p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-serif text-2xl text-white">Reservations</h2>
          <div className="flex items-center gap-4">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger 
                className="w-[180px] bg-white/5 border-white/10 text-white"
                data-testid="status-filter-select"
              >
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent className="bg-[#0A0A0A] border-white/10 z-[100]">
                <SelectItem value="all" className="text-white hover:bg-white/10 focus:bg-white/10 cursor-pointer">All Bookings</SelectItem>
                <SelectItem value="pending" className="text-white hover:bg-white/10 focus:bg-white/10 cursor-pointer">Pending</SelectItem>
                <SelectItem value="confirmed" className="text-white hover:bg-white/10 focus:bg-white/10 cursor-pointer">Confirmed</SelectItem>
                <SelectItem value="completed" className="text-white hover:bg-white/10 focus:bg-white/10 cursor-pointer">Completed</SelectItem>
                <SelectItem value="cancelled" className="text-white hover:bg-white/10 focus:bg-white/10 cursor-pointer">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Bookings Table */}
        <div className="glass-light rounded-md overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="loading-spinner" />
            </div>
          ) : bookings.length === 0 ? (
            <div className="text-center py-20">
              <Car className="w-16 h-16 text-white/20 mx-auto mb-4" />
              <p className="text-white/50 text-lg">No bookings found</p>
              <p className="text-white/30 text-sm mt-2">
                {statusFilter !== "all" ? "Try changing the filter" : "Bookings will appear here"}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="admin-table" data-testid="bookings-table">
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th>Route</th>
                    <th>Date & Time</th>
                    <th>Distance</th>
                    <th>Price</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((booking) => (
                    <tr key={booking.id} data-testid={`booking-row-${booking.id}`}>
                      <td>
                        <div>
                          <p className="text-white font-medium">{booking.customer_name}</p>
                          <p className="text-white/50 text-sm">{booking.customer_email}</p>
                          <p className="text-white/40 text-xs">{booking.customer_phone}</p>
                        </div>
                      </td>
                      <td>
                        <div className="space-y-1 max-w-xs">
                          <div className="flex items-start gap-2">
                            <MapPin className="w-3 h-3 text-[#D4AF37] mt-1 flex-shrink-0" />
                            <p className="text-white text-xs truncate">{booking.pickup_address}</p>
                          </div>
                          <div className="flex items-start gap-2">
                            <Navigation className="w-3 h-3 text-white/40 mt-1 flex-shrink-0" />
                            <p className="text-white/60 text-xs truncate">{booking.dropoff_address}</p>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="flex items-start gap-2">
                          <Calendar className="w-4 h-4 text-white/40 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-white text-sm">{booking.pickup_date}</p>
                            <p className="text-white/50 text-xs">{booking.pickup_time}</p>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div>
                          <p className="text-white text-sm">{booking.distance_miles} mi</p>
                          <p className="text-white/50 text-xs">{booking.duration_minutes} min</p>
                        </div>
                      </td>
                      <td>
                        <p className="price-display text-xl">${booking.total_price}</p>
                      </td>
                      <td>{getStatusBadge(booking.status)}</td>
                      <td>
                        <div className="flex items-center gap-2">
                          <Select 
                            value={booking.status}
                            onValueChange={(value) => updateStatus(booking.id, value)}
                          >
                            <SelectTrigger 
                              className="w-[130px] h-8 bg-white/5 border-white/10 text-white text-xs"
                              data-testid={`status-select-${booking.id}`}
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-[#0A0A0A] border-white/10 z-[100]">
                              <SelectItem value="pending" className="text-white hover:bg-white/10 focus:bg-white/10 cursor-pointer">Pending</SelectItem>
                              <SelectItem value="confirmed" className="text-white hover:bg-white/10 focus:bg-white/10 cursor-pointer">Confirmed</SelectItem>
                              <SelectItem value="completed" className="text-white hover:bg-white/10 focus:bg-white/10 cursor-pointer">Completed</SelectItem>
                              <SelectItem value="cancelled" className="text-white hover:bg-white/10 focus:bg-white/10 cursor-pointer">Cancelled</SelectItem>
                            </SelectContent>
                          </Select>
                          <button
                            onClick={() => deleteBooking(booking.id)}
                            className="p-2 text-white/40 hover:text-red-500 transition-colors"
                            data-testid={`delete-btn-${booking.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
